import type { Coordinate, PlacedSign } from '@orgsim/shared';
import L from 'leaflet';
import { useEffect, useMemo, useRef } from 'react';
import { Marker } from 'react-leaflet';
import { signIcon } from '../signs/svg';
import { MarkerConnector, useMarkerPosition } from './MarkerLayout';
import { fromLatLng, toLatLng } from './crs';
import { iconRadius, moveFromDisplay } from './markerGeometry';

type Props = {
  sign: PlacedSign;
  draggable?: boolean;
  onMove?: (position: Coordinate) => void;
  onClick?: () => void;
  selected?: boolean;
  onContextMenu?: (sign: PlacedSign, screen: { x: number; y: number }) => void;
};

export function SignMarker({
  sign,
  draggable = true,
  onMove,
  onContextMenu,
  onClick,
  selected,
}: Props) {
  const rendered = useMemo(
    () => signIcon(sign.signType, { label: sign.label, organisation: sign.organisation }),
    [sign.signType, sign.label, sign.organisation],
  );
  const radius = iconRadius(rendered.width, rendered.height, rendered.anchor);
  const displayed = useMarkerPosition(`sign:${sign.id}`, sign.position, radius);
  const markerRef = useRef<L.Marker>(null);
  const icon = useMemo(
    () =>
      L.divIcon({
        html: rendered.svg,
        iconSize: [rendered.width, rendered.height],
        iconAnchor: rendered.anchor,
        className: `leaflet-div-icon sign-marker${selected ? ' marker-selected' : ''}`,
      }),
    [rendered, selected],
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: Leaflet can recreate the element when the icon changes.
  useEffect(() => {
    markerRef.current?.getElement()?.setAttribute('aria-label', sign.label ?? sign.signType);
  }, [sign.label, sign.signType, icon]);
  return (
    <>
      <MarkerConnector actual={sign.position} displayed={displayed} />
      <Marker
        ref={markerRef}
        position={toLatLng(displayed)}
        icon={icon}
        draggable={draggable}
        title={sign.label ?? sign.signType}
        alt={sign.label ?? sign.signType}
        zIndexOffset={selected ? 1000 : 0}
        eventHandlers={{
          click: () => onClick?.(),
          dragend: (e) =>
            onMove?.(
              moveFromDisplay(
                sign.position,
                displayed,
                fromLatLng((e.target as L.Marker).getLatLng()),
              ),
            ),
          contextmenu: (e) =>
            onContextMenu?.(sign, { x: e.originalEvent.clientX, y: e.originalEvent.clientY }),
        }}
      />
    </>
  );
}
